import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  MenuItem,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { useSnackbar } from "../../hooks/useSnackbar";
import { Panel, FormGrid, PrimaryButton, GhostButton } from "./ui";
import {
  BALANCE_TYPES,
  BALANCE_LOAD_TYPES,
  PAY_RESULT_SUCCESS,
  formatKurus,
  balanceTypeLabel,
  DP,
} from "./constants";
import { loadCardBallance, getPersonal } from "../../api/digitalPayAPI";
import {
  BALANCE_TEMPLATE,
  downloadTemplate,
  readSheet,
  missingHeaders,
} from "./excelTemplates";
import {
  createAndDownloadExcel,
  generateExcelFilename,
} from "../../utils/excelExportUtils";
import { isSuperAdminUser } from "./roleUtils";

const ALL = ""; // filtrelerde "Tümü"
const EXPORT_FILTERS = { firm: ALL, dept: ALL, status: "1" }; // varsayılan: aktif personel

export default function PaymentsPanel({ lookups }) {
  const { firms = [], departments = [] } = lookups;
  const canSelectFirm = isSuperAdminUser();
  const assignedFirmName = canSelectFirm ? "" : (firms[0]?.name ?? "");
  const { snackbar, showSnackbar, hideSnackbar } = useSnackbar();

  const [single, setSingle] = useState({
    sicilNo: "",
    firmName: assignedFirmName,
    balanceType: BALANCE_TYPES[0].value,
    balanceLoadType: BALANCE_LOAD_TYPES[0].value,
    price: "",
  });
  const [busy, setBusy] = useState(false);

  const [mass, setMass] = useState({
    firmName: assignedFirmName,
    balanceType: BALANCE_TYPES[0].value,
    balanceLoadType: BALANCE_LOAD_TYPES[0].value,
  });
  const [bulk, setBulk] = useState(null);
  // Onay dialogu: { title, rows:[{label,value}], confirmLabel, onConfirm }
  const [confirm, setConfirm] = useState(null);
  // Personel listesinden bakiye şablonu üretme dialogu
  const [exportOpen, setExportOpen] = useState(false);
  const [exportFilters, setExportFilters] = useState(EXPORT_FILTERS);
  const [exporting, setExporting] = useState(false);

  const loadTypeLabel = (value) =>
    BALANCE_LOAD_TYPES.find((o) => o.value === Number(value))?.label ?? "-";

  useEffect(() => {
    if (!canSelectFirm) {
      setSingle((current) => ({ ...current, firmName: assignedFirmName }));
      setMass((current) => ({ ...current, firmName: assignedFirmName }));
    }
  }, [assignedFirmName, canSelectFirm]);

  // --- Tekil yükleme ---
  // Önce onay dialogu; kullanıcı seçimlerini gösterip "emin misiniz?" sorar.
  const askSingleLoad = () => {
    // Alan zaten kuruş: TL çevrimi yok.
    const price = Number(single.price);
    if (!single.sicilNo || !single.firmName || price <= 0) {
      showSnackbar("Sicil no, firma ve geçerli bir tutar zorunlu", "error");
      return;
    }
    setConfirm({
      title: "Bakiye Yükleme Onayı",
      rows: [
        { label: "Sicil No", value: single.sicilNo },
        { label: "Firma", value: single.firmName },
        { label: "Bakiye Tipi", value: balanceTypeLabel(single.balanceType) },
        { label: "Devir Tipi", value: loadTypeLabel(single.balanceLoadType) },
        { label: "Tutar", value: formatKurus(price) },
      ],
      confirmLabel: "Yükle",
      onConfirm: () => doSingleLoad(price),
    });
  };

  const doSingleLoad = async (price) => {
    setBusy(true);
    try {
      const res = await loadCardBallance({
        balanceType: Number(single.balanceType),
        balanceLoadType: Number(single.balanceLoadType),
        sicilNo: single.sicilNo,
        firmName: single.firmName,
        price,
      });
      if (res?.result !== PAY_RESULT_SUCCESS) {
        showSnackbar("Bakiye yüklenemedi", "error");
        return;
      }
      showSnackbar(
        `Bakiye yüklendi. Standart: ${formatKurus(res.remainingBalance)} · İSG: ${formatKurus(res.remainingIsgBalance)}`,
        "success"
      );
      setSingle({ ...single, sicilNo: "", price: "" });
    } catch (e) {
      showSnackbar(e.message || "Bakiye yüklenemedi", "error");
    } finally {
      setBusy(false);
    }
  };

  // --- Toplu yükleme (BakiyeYüklemeŞablonu.xlsx) ---
  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (!mass.firmName) {
      showSnackbar("Önce firma seçin — şablonda firma sütunu yok", "error");
      return;
    }

    let sheet;
    try {
      sheet = await readSheet(file);
    } catch (err) {
      showSnackbar(err.message, "error");
      return;
    }

    const missing = missingHeaders(sheet, BALANCE_TEMPLATE);
    if (missing.length) {
      showSnackbar(`Şablon uyumsuz. Eksik sütun: ${missing.join(", ")}`, "error");
      return;
    }

    setConfirm({
      title: "Toplu Bakiye Yükleme Onayı",
      rows: [
        { label: "Firma", value: mass.firmName },
        { label: "Bakiye Tipi", value: balanceTypeLabel(mass.balanceType) },
        { label: "Devir Tipi", value: loadTypeLabel(mass.balanceLoadType) },
        { label: "Kayıt Sayısı", value: `${sheet.length} kart` },
      ],
      confirmLabel: "Yükle",
      onConfirm: () => doBulkLoad(sheet),
    });
  };

  const doBulkLoad = async (sheet) => {
    let ok = 0;
    const failed = [];
    setBulk({ done: 0, total: sheet.length });

    // Sıralı: LoadCardBallance döngüsü (demo-ui ile aynı akış).
    for (let i = 0; i < sheet.length; i++) {
      const r = sheet[i];
      // Şablondaki tutar zaten kuruş ("Bakiye(Kuruş)"), tekrar çevirmiyoruz.
      const price = Math.round(Number(r["Bakiye(Kuruş)"]));
      try {
        if (!Number.isFinite(price) || price <= 0) throw new Error("Geçersiz tutar");
        const res = await loadCardBallance({
          balanceType: Number(mass.balanceType),
          balanceLoadType: Number(mass.balanceLoadType),
          sicilNo: String(r["Sicil No"]),
          firmName: mass.firmName,
          price,
        });
        if (res?.result !== PAY_RESULT_SUCCESS) throw new Error("Reddedildi");
        ok++;
      } catch (err) {
        failed.push(`${r["Sicil No"]}: ${err.message}`);
      }
      setBulk({ done: i + 1, total: sheet.length });
    }

    setBulk(null);

    if (failed.length) {
      console.warn("[Bakiye toplu yükleme] hatalı satırlar:", failed);
      showSnackbar(
        `Toplu yükleme bitti. Başarılı: ${ok}, hatalı: ${failed.length}. İlk hata: ${failed[0]}`,
        "warning"
      );
    } else {
      showSnackbar(`Toplu yükleme tamamlandı. ${ok} karta bakiye yüklendi.`, "success");
    }
  };

  // --- Personel listesinden bakiye yükleme şablonu üret ---
  // Bakiye sütunu 0 iner; kullanıcı yüklenecek tutarı kendisi yazıp geri yükler.
  const handleExportPersonal = async () => {
    setExporting(true);
    try {
      const people = await getPersonal();
      const selected = people.filter((p) => {
        if (
          exportFilters.firm !== ALL &&
          Number(p.firm_idfirm) !== Number(exportFilters.firm)
        )
          return false;
        if (
          exportFilters.dept !== ALL &&
          Number(p.department_iddepartment) !== Number(exportFilters.dept)
        )
          return false;
        if (
          exportFilters.status !== ALL &&
          Number(p.isActive) !== Number(exportFilters.status)
        )
          return false;
        return true;
      });

      if (!selected.length) {
        showSnackbar("Seçilen filtrelerle personel bulunamadı", "warning");
        return;
      }

      const result = createAndDownloadExcel(
        selected,
        [
          { field: "regNumber", headerName: BALANCE_TEMPLATE.headers[0] },
          { field: "name", headerName: BALANCE_TEMPLATE.headers[1] },
          { field: "surname", headerName: BALANCE_TEMPLATE.headers[2] },
          { field: "cardBalance", headerName: BALANCE_TEMPLATE.headers[3] },
        ],
        BALANCE_TEMPLATE.sheetName,
        generateExcelFilename("Bakiye_Yukleme"),
        {
          valueTransformer: (value, column) =>
            column.field === "cardBalance" ? 0 : (value ?? ""),
        }
      );

      if (result.success) {
        showSnackbar(
          `${selected.length} personel indirildi: ${result.fileName}`,
          "success"
        );
        setExportOpen(false);
      } else {
        showSnackbar(result.error || "Dosya indirilemedi", "error");
      }
    } catch (e) {
      showSnackbar(e.message || "Personel listesi alınamadı", "error");
    } finally {
      setExporting(false);
    }
  };

  const exportDepartments = departments.filter(
    (d) =>
      exportFilters.firm === ALL ||
      Number(d.firm_idfirm) === Number(exportFilters.firm)
  );

  const firmSelect = (value, onChange, label = "Firma") =>
    canSelectFirm ? (
      <TextField select label={label} value={value} onChange={onChange} size="small" fullWidth>
        {firms.map((f) => (
          <MenuItem key={f.idfirm} value={f.name}>
            {f.name}
          </MenuItem>
        ))}
      </TextField>
    ) : null;

  return (
    <>
      <Panel
        title="Tekil Bakiye Yükleme"
        sub="Seçtiğiniz karta güvenli bir şekilde bakiye yükleyebilirsiniz."
      >
        <FormGrid>
          <TextField
            label="Sicil No"
            value={single.sicilNo}
            onChange={(e) => setSingle({ ...single, sicilNo: e.target.value })}
            size="small"
            required
            fullWidth
          />
          {firmSelect(single.firmName, (e) =>
            setSingle({ ...single, firmName: e.target.value })
          )}
          <TextField
            select
            label="Bakiye Tipi"
            value={single.balanceType}
            onChange={(e) => setSingle({ ...single, balanceType: e.target.value })}
            size="small"
            fullWidth
          >
            {BALANCE_TYPES.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Devir Tipi"
            value={single.balanceLoadType}
            onChange={(e) => setSingle({ ...single, balanceLoadType: e.target.value })}
            size="small"
            fullWidth
          >
            {BALANCE_LOAD_TYPES.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Tutar (Kuruş)"
            value={single.price}
            // Backend kuruş bekliyor; alan yalnızca tam sayı kabul eder.
            onChange={(e) =>
              setSingle({ ...single, price: e.target.value.replace(/\D/g, "") })
            }
            size="small"
            fullWidth
            inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
            helperText={
              single.price
                ? `= ${formatKurus(Number(single.price))}`
                : "Kuruş cinsinden tam sayı — örn. 480 = 4,80 ₺"
            }
          />
        </FormGrid>

        <Box sx={{ mt: 2.5 }}>
          <PrimaryButton onClick={askSingleLoad} disabled={busy}>
            Bakiye Yükle
          </PrimaryButton>
        </Box>
      </Panel>

      <Panel
        title="Toplu Bakiye Yükleme"
        sub="Excel ile birden fazla karta tek seferde bakiye yükleyin. Hazır listeyi indirip tutarları yazın ya da doldurduğunuz dosyayı yükleyin."
        action={
          <GhostButton
            startIcon={<DownloadIcon />}
            onClick={() => downloadTemplate(BALANCE_TEMPLATE)}
          >
            Şablonu İndir
          </GhostButton>
        }
      >
        <FormGrid>
          {firmSelect(
            mass.firmName,
            (e) => setMass({ ...mass, firmName: e.target.value }),
            "Firma (zorunlu)"
          )}
          <TextField
            select
            label="Bakiye Tipi"
            value={mass.balanceType}
            onChange={(e) => setMass({ ...mass, balanceType: e.target.value })}
            size="small"
            fullWidth
          >
            {BALANCE_TYPES.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Devir Tipi"
            value={mass.balanceLoadType}
            onChange={(e) => setMass({ ...mass, balanceLoadType: e.target.value })}
            size="small"
            fullWidth
          >
            {BALANCE_LOAD_TYPES.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
        </FormGrid>

        <Typography sx={{ fontSize: "0.8rem", color: DP.textMuted, mt: 2 }}>
          Şablondaki tutar sütunu <strong>kuruş</strong> cinsindendir (480 = 4,80 ₺).
          {canSelectFirm
            ? " Firma sütunu şablonda yok, yukarıdan seçilir."
            : " Firma hesabınıza göre otomatik belirlenir."}
        </Typography>

        <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
          <PrimaryButton component="label" startIcon={<UploadFileIcon />} disabled={!!bulk}>
            Excel Yükle
            <input type="file" accept=".xlsx,.xls" hidden onChange={handleUpload} />
          </PrimaryButton>
          <PrimaryButton
            startIcon={<DownloadIcon />}
            disabled={!!bulk}
            onClick={() => {
              setExportFilters(EXPORT_FILTERS);
              setExportOpen(true);
            }}
            sx={{
              bgcolor: DP.success,
              boxShadow: "0 8px 18px rgba(16,185,129,.22)",
              "&:hover": { bgcolor: "#059669" },
            }}
          >
            Personellerin Bakiye Yükleme Excel İndir
          </PrimaryButton>
        </Box>

        {bulk && (
          <Box sx={{ mt: 2 }}>
            <Typography sx={{ fontSize: "0.85rem", color: DP.textMuted, mb: 0.8 }}>
              Yükleniyor… {bulk.done} / {bulk.total}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={(bulk.done / bulk.total) * 100}
              sx={{ borderRadius: 999 }}
            />
          </Box>
        )}
      </Panel>

      <Dialog
        open={!!confirm}
        onClose={() => setConfirm(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>{confirm?.title}</DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ mb: 1.5, color: DP.textMuted, fontSize: "0.9rem" }}>
            Aşağıdaki bilgilerle işlemi onaylıyor musunuz?
          </Typography>
          {confirm?.rows.map((row) => (
            <Box
              key={row.label}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                gap: 2,
                py: 0.6,
                borderBottom: `1px solid ${DP.border}`,
              }}
            >
              <Typography sx={{ color: DP.textMuted }}>{row.label}</Typography>
              <Typography sx={{ fontWeight: 600, textAlign: "right" }}>
                {row.value}
              </Typography>
            </Box>
          ))}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <GhostButton onClick={() => setConfirm(null)}>İptal</GhostButton>
          <PrimaryButton
            onClick={() => {
              const action = confirm?.onConfirm;
              setConfirm(null);
              if (action) action();
            }}
          >
            {confirm?.confirmLabel || "Onayla"}
          </PrimaryButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={exportOpen}
        onClose={() => !exporting && setExportOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          Personellerin Bakiye Yükleme Excel'i
        </DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ mb: 2, color: DP.textMuted, fontSize: "0.9rem" }}>
            Seçtiğiniz personeller, toplu bakiye yüklemeye uyumlu şablonla iner.
            Bakiye sütunu 0 gelir; yüklenecek tutarları yazıp dosyayı geri yükleyin.
          </Typography>
          <FormGrid>
            {canSelectFirm && (
              <TextField
                select
                label="Firma"
                value={exportFilters.firm}
                onChange={(e) =>
                  // Firma değişince departman seçimi geçersiz kalabilir; sıfırlanır.
                  setExportFilters({
                    ...exportFilters,
                    firm: e.target.value,
                    dept: ALL,
                  })
                }
                size="small"
                fullWidth
              >
                <MenuItem value={ALL}>Tümü</MenuItem>
                {firms.map((f) => (
                  <MenuItem key={f.idfirm} value={f.idfirm}>
                    {f.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <TextField
              select
              label="Departman"
              value={exportFilters.dept}
              onChange={(e) =>
                setExportFilters({ ...exportFilters, dept: e.target.value })
              }
              size="small"
              fullWidth
            >
              <MenuItem value={ALL}>Tümü</MenuItem>
              {exportDepartments.map((d) => (
                <MenuItem key={d.iddepartment} value={d.iddepartment}>
                  {d.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Durum"
              value={exportFilters.status}
              onChange={(e) =>
                setExportFilters({ ...exportFilters, status: e.target.value })
              }
              size="small"
              fullWidth
            >
              <MenuItem value={ALL}>Tümü</MenuItem>
              <MenuItem value="1">Aktif</MenuItem>
              <MenuItem value="0">Pasif</MenuItem>
            </TextField>
          </FormGrid>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <GhostButton onClick={() => setExportOpen(false)} disabled={exporting}>
            İptal
          </GhostButton>
          <PrimaryButton onClick={handleExportPersonal} disabled={exporting}>
            {exporting ? "Hazırlanıyor…" : "Excel İndir"}
          </PrimaryButton>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={8000}
        onClose={hideSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={hideSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
